import React, { useState, useEffect, memo } from "react";
import { DatePicker, Button, Spin, Skeleton, message, Space, Table, Tag, Tooltip, Select, Segmented } from "antd";
import {
  Treemap,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { ReloadOutlined, FireOutlined, ClockCircleOutlined, QuestionCircleOutlined, TableOutlined, AppstoreOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { getSemanticTopics } from "../../api/api";
import type { SemanticTopicsResponse, SemanticTopic } from "../../types/api-models";

const { RangePicker } = DatePicker;

type AggregationType = 'daily' | 'weekly' | 'monthly';
type ViewType = 'treemap' | 'table';

interface SemanticTopicAnalysisProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const SemanticTopicAnalysis: React.FC<SemanticTopicAnalysisProps> = ({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SemanticTopicsResponse | null>(null);
  const [aggregation, setAggregation] = useState<AggregationType>('daily');
  const [viewType, setViewType] = useState<ViewType>('treemap');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [topicLimit, setTopicLimit] = useState(isExpanded ? 15 : 10);

  useEffect(() => {
    // For collapsed view with dashboard data, use it and skip fetching
    if (!isExpanded && dashboardData?.semantic_topics) {
      // Normalize dashboard data (list) to match API response shape (object)
      const raw = dashboardData.semantic_topics;
      const normalized = Array.isArray(raw) ? {
        topics: raw,
        total_questions: 0,
        date_range: { from: '', to: '' },
        aggregation_used: 'daily'
      } : raw;
      
      setData(normalized);
      setLoading(false);
      return;
    }
    
    // Skip fetch in collapsed view if no dashboard data yet (wait for it)
    if (!isExpanded) {
      return;
    }
    
    // Expanded mode - DON'T auto-load, reset to empty state
    setData(null);
    setLoading(false);
  }, [isExpanded, dashboardData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let from_date: string;
      let to_date: string;
      
      // Calculate date range based on aggregation
      if (aggregation === 'daily') {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
          message.warning('Please select a date range');
          setLoading(false);
          return;
        }
        from_date = dateRange[0].format('YYYY-MM-DD');
        to_date = dateRange[1].format('YYYY-MM-DD');
      } else if (aggregation === 'weekly') {
        // Last 52 weeks
        const end = dayjs();
        const start = end.subtract(52, 'week');
        from_date = start.format('YYYY-MM-DD');
        to_date = end.format('YYYY-MM-DD');
      } else {
        // Monthly - last 12 months
        const end = dayjs();
        const start = end.subtract(12, 'month');
        from_date = start.format('YYYY-MM-DD');
        to_date = end.format('YYYY-MM-DD');
      }
      
      const response = await getSemanticTopics(from_date, to_date, topicLimit);
      setData(response);
    } catch (error) {
      message.error('Failed to load semantic topics');
      console.error(error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Intent color mapping
  const getIntentColor = (intent: string) => {
    const colors: Record<string, string> = {
      eligibility: 'blue',
      procedural: 'green',
      status: 'orange',
      complaint: 'red',
      calculation: 'purple'
    };
    return colors[intent] || 'default';
  };

  // Urgency icon and color
  const getUrgencyIndicator = (urgency: string) => {
    if (urgency === 'high') {
      return <Tooltip title="High urgency"><FireOutlined style={{ color: '#ff4d4f' }} /></Tooltip>;
    } else if (urgency === 'medium') {
      return <Tooltip title="Medium urgency"><ClockCircleOutlined style={{ color: '#faad14' }} /></Tooltip>;
    } else {
      return <Tooltip title="Low urgency"><ClockCircleOutlined style={{ color: '#52c41a' }} /></Tooltip>;
    }
  };

  // Skeleton loader for collapsed view
  if (loading && !isExpanded) {
    return (
      <div style={{ padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  // Show loading spinner
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading analysis..." />
      </div>
    );
  }

  // --- CHART RENDERING HELPERS ---

  const hasData = data && data.topics.length > 0;

  // Treemap Custom Content
  const CustomizedContent: React.FC<any> = (props: any) => {
    const { x, y, width, height, name, count, index } = props;
    
    // Validate required props
    if (typeof x !== 'number' || typeof y !== 'number' || typeof width !== 'number' || typeof height !== 'number') {
      return null;
    }
    
    const COLORS = ['#8884d8', '#83a6ed', '#8dd1e1', '#82ca9d', '#a4de6c', '#ffc658', '#ff8042', '#ffbb28', '#d084d0', '#8a89a6', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#dfe6e9', '#74b9ff', '#a29bfe', '#fd79a8', '#fdcb6e', '#e17055'];
    
    // Calculate block area
    const area = width * height;
    const minArea = 1200; // Minimum area to show text
    const fillColor = COLORS[index % COLORS.length] || '#8884d8';

    if (area < minArea) {
      return (
        <g>
          <rect x={x} y={y} width={width} height={height} style={{ fill: fillColor, stroke: '#fff', strokeWidth: 1 }} />
        </g>
      );
    }
    
    // Adaptive font size based on block area
    let fontSize = Math.max(6, Math.min(12, Math.sqrt(area) / 10));
    if (area > 5000) fontSize = Math.max(8, Math.min(14, Math.sqrt(area) / 8));
    
    // Contrast calculation
    const getTextColor = (bgColor: string) => {
      const hex = bgColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness < 140 ? '#ffffff' : '#1a1a1a';
    };
    
    const textColor = getTextColor(fillColor);

    // Text wrapping logic
    const wrapText = (text: string | undefined, maxWidth: number, fSize: number): string[] => {
      if (!text || typeof text !== 'string') return ['No Title'];
      const words = text.split(' ');
      const lines: string[] = [];
      let currentLine = '';
      
      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const estimatedWidth = testLine.length * fSize * 0.6;
        if (estimatedWidth <= maxWidth - 10) {
          currentLine = testLine;
        } else {
          if (currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            const truncated = word.substring(0, Math.floor((maxWidth - 10) / (fSize * 0.6)));
            lines.push(truncated + '...');
            currentLine = '';
          }
        }
      }
      if (currentLine) lines.push(currentLine);
      return lines;
    };

    const wrappedLines = wrapText(name, width, fontSize);
    const lineHeight = fontSize * 1.2;
    const totalTextHeight = wrappedLines.length * lineHeight + fontSize + 8;
    
    // If text doesn't fit vertically, reduce font size
    if (totalTextHeight > height - 8) {
      fontSize = Math.max(8, (height - 16) / (wrappedLines.length + 1.5));
      // Re-wrap with new font size? Usually fine to just truncate or hide. 
      // For safety, just show rect:
      // return <g><rect ... /></g> 
      // But let's keep it simple for now as per original.
    }

    return (
      <g>
        <rect x={x} y={y} width={width} height={height} style={{ fill: fillColor, stroke: '#fff', strokeWidth: 1 }} />
        {wrappedLines.map((line, i) => (
          <text
            key={i}
            x={x + width / 2}
            y={y + (height - totalTextHeight) / 2 + (i + 1) * lineHeight}
            textAnchor="middle"
            fill={textColor}
            fontSize={fontSize}
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
            style={{ pointerEvents: 'none' }}
          >
            {line}
          </text>
        ))}
        <text
          x={x + width / 2}
          y={y + height - 8}
          textAnchor="middle"
          fill={textColor}
          fontSize={fontSize}
          fontWeight="bold"
          style={{ fontFamily: 'Arial, sans-serif', pointerEvents: 'none' }}
        >
          {count || 0}
        </text>
      </g>
    );
  };

  // Empty state handling
  let emptyStateMessage;
  if (!hasData && isExpanded) {
    emptyStateMessage = (
      <div style={{ textAlign: 'center', padding: 60, color: '#666' }}>
        <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to analyze semantic topics</p>
        <p style={{ fontSize: 13, color: '#999' }}>Adjust filters above and click "Apply Filters" to view data</p>
      </div>
    );
  } else if (!hasData) {
    emptyStateMessage = (
      <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
        No semantic topics available
      </div>
    );
  }

  // --- CONTENT PREPARATION ---
  
  let contentComponent;
  
  if (!hasData) {
    contentComponent = emptyStateMessage;
  } else {
    if (viewType === 'treemap') {
      // Treemap View
      const treemapData = data!.topics.map(topic => ({
        name: topic.topic_name,
        size: topic.count,
        count: topic.count,
        intent: topic.intent,
        urgency: topic.urgency
      }));

      contentComponent = (
        <div style={{ height: isExpanded ? 400 : 240, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={treemapData}
              dataKey="size"
              stroke="#fff"
              fill="#8884d8"
              content={<CustomizedContent />}
            >
              <RechartsTooltip
                formatter={(value: number, name: string, props: any) => [
                  `${props.payload.count} questions`,
                  props.payload.name
                ]}
                contentStyle={{ fontSize: 12 }}
              />
            </Treemap>
          </ResponsiveContainer>
        </div>
      );
    } else {
      // Table View
      const columns = [
        {
          title: 'Business Topic',
          dataIndex: 'topic_name',
          key: 'topic_name',
          width: isExpanded ? '35%' : '50%',
          render: (text: string, record: SemanticTopic) => (
            <Space direction="vertical" size={0}>
              <strong style={{ fontSize: 14 }}>{text}</strong>
              {isExpanded && record.example_question && (
                <Tooltip title={record.example_question}>
                  <span style={{ fontSize: 12, color: '#999', display: 'block', marginTop: 4 }}>
                    <QuestionCircleOutlined /> {record.example_question.substring(0, 60)}...
                  </span>
                </Tooltip>
              )}
            </Space>
          ),
        },
        {
          title: 'Intent',
          dataIndex: 'intent',
          key: 'intent',
          width: '15%',
          render: (intent: string) => (
            <Tag color={getIntentColor(intent)} style={{ textTransform: 'capitalize' }}>
              {intent}
            </Tag>
          ),
        },
        {
          title: 'Urgency',
          dataIndex: 'urgency',
          key: 'urgency',
          width: '12%',
          render: (urgency: string) => (
            <Space>
              {getUrgencyIndicator(urgency)}
              <span style={{ textTransform: 'capitalize' }}>{urgency}</span>
            </Space>
          ),
        },
        {
          title: 'Volume',
          dataIndex: 'count',
          key: 'count',
          width: '15%',
          align: 'right' as const,
          render: (count: number) => (
            <strong style={{ fontSize: 16, color: '#1890ff' }}>{count.toLocaleString()}</strong>
          ),
        },
      ];

      // Add variations column only in expanded view
      if (isExpanded) {
        columns.splice(1, 0, {
          title: 'User Phrases',
          dataIndex: 'variations',
          key: 'variations',
          width: '25%',
          render: (_text: any, record: SemanticTopic) => (
            <div>
              {record.variations?.slice(0, 3).map((variation: string, idx: number) => (
                <Tag key={idx} color={getIntentColor(record.intent)} style={{ marginBottom: 4 }}>{variation}</Tag>
              ))}
              {record.variations && record.variations.length > 3 && (
                <Tooltip title={record.variations.slice(3).join(', ')}>
                  <Tag color={getIntentColor(record.intent)}>+{record.variations.length - 3} more</Tag>
                </Tooltip>
              )}
            </div>
          ),
        });
      }

      contentComponent = (
        <Table
          dataSource={data!.topics}
          columns={columns}
          loading={loading}
          rowKey="topic_name"
          pagination={isExpanded ? { pageSize: 10 } : false}
          size="middle"
          scroll={{ y: isExpanded ? undefined : 200 }}
        />
      );
    }
  }

  // If not expanded, return content only
  if (!isExpanded) {
    return contentComponent;
  }

  // Expanded view with controls
  return (
    <div>
      {/* Controls Row */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space wrap>
          <span style={{ fontSize: 13, color: '#666' }}>View:</span>
          <Segmented
            value={viewType}
            onChange={(value) => setViewType(value as ViewType)}
            options={[
              { label: 'Treemap', value: 'treemap', icon: <AppstoreOutlined /> },
              { label: 'Topic List', value: 'table', icon: <TableOutlined /> }
            ]}
          />
          
          <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Aggregation:</span>
          <Segmented
            value={aggregation}
            onChange={(value) => setAggregation(value as AggregationType)}
            options={[
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' }
            ]}
          />
          {aggregation === 'daily' ? (
            <>
              <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Range:</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]]);
                  }
                }}
                format="YYYY-MM-DD"
                style={{ width: 240 }}
                disabledDate={(current) => {
                  if (!current) return false;
                  return current.isAfter(dayjs().endOf('day'));
                }}
              />
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
              Last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
            </span>
          )}
          
          <Select
            value={topicLimit}
            onChange={setTopicLimit}
            style={{ width: 100 }}
            options={[
              { label: 'Top 5', value: 5 },
              { label: 'Top 10', value: 10 },
              { label: 'Top 20', value: 20 },
              { label: 'Top 50', value: 50 },
            ]}
          />
        </Space>
        
        <Space>
          <Button type="primary" onClick={fetchData} loading={loading}>
            Apply
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchData} disabled={!hasData}>
            Refresh
          </Button>
        </Space>
      </Space>

      {/* Content */}
      {contentComponent}
      
      {/* Info footer */}
      {hasData && data && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#999', textAlign: 'center' }}>
          Analyzed {data.total_questions.toLocaleString()} questions • 
          Aggregation: {data.aggregation_used} • 
          Date range: {dayjs(data.date_range.from).format('MMM D, YYYY')} - {dayjs(data.date_range.to).format('MMM D, YYYY')}
        </div>
      )}
    </div>
  );
};

export default memo(SemanticTopicAnalysis);
